import { User } from "oidc-client-ts";
import { doGet, doPut } from "./util/apiHelper.ts";
import {
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import { TeacherStatus } from "./userApi.ts";
import { LocationType } from "./locationApi.ts";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { InstrumentWithFilters } from "./instrumentApi.ts";
import {
  LOCATION_QUERY_KEYS,
  NEEDED_ACTIONS_KEY,
  TEACHER_QUERY_KEYS,
} from "./util/queryKeys.ts";

type TeacherActionType = "INFO" | "WARNING" | "URGENT";

export interface TeacherDto {
  id: string;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
  languages: string[];
  instruments: string[];
  genres: string[];
  teacherStatus?: TeacherStatus;
  introduction?: string;
}

export interface TeacherLocation {
  id: string;
  name: string;
  countryCode?: string;
  postalCode?: string;
  city?: string;
  region?: string;
  timeZone: string;
  instruments: InstrumentWithFilters[];
  locationType: LocationType;
}

export interface TeacherLocationsResponse {
  locations: TeacherLocation[];
}

interface TeacherAction {
  id: string;
  type: TeacherActionType;
  hasLink: boolean;
}

export interface TeacherActionsResponseDto {
  teacherActions: TeacherAction[];
}

export interface TeacherActionResponse {
  url: string;
}

export interface TeacherDashboardLinkDto {
  url: string;
}

export type TeacherLessonPriceType = "BOOKING" | "SUBSCRIPTION";

export interface TeacherLessonPrice {
  durationInMinutes: number;
  price: number;
  currency: string;
  type: TeacherLessonPriceType;
}

export interface TeacherLessonPricesDto {
  prices: TeacherLessonPrice[];
}

export interface TeacherFeedbackDto {
  text: string;
}

export interface TeacherFeedbackResponse {
  id: string;
  text: string;
  name: string;
  profilePictureUrl?: string;
  modifiedAt: string;
  userId: string;
}

export interface TeacherFeedbackResponseDto {
  teacherFeedback: TeacherFeedbackResponse[];
}

export const getTeacher = async (
  teacherId: string,
  user?: User | null,
): Promise<TeacherDto> => {
  return await doGet<TeacherDto>(`/public/teachers/${teacherId}`, user);
};

export const getTeacherLocations = async (
  user: User,
  teacherId: string,
): Promise<TeacherLocationsResponse> => {
  return await doGet<TeacherLocationsResponse>(
    `/teachers/${teacherId}/locations`,
    user,
  );
};

export const getNeededTeacherActions = async (
  user: User,
  isRedirected?: boolean,
): Promise<TeacherActionsResponseDto> => {
  return await doGet<TeacherActionsResponseDto>(
    `/teachers/${user.profile.sub}/needed-actions?isRedirected=${isRedirected ? isRedirected : false}`,
    user,
  );
};

export const getTeacherLessonPrices = async (
  teacherId: string,
  user: User,
  type?: TeacherLessonPriceType,
): Promise<TeacherLessonPricesDto> => {
  const result = await doGet<TeacherLessonPricesDto>(
    `/teachers/${teacherId}/lesson-prices`,
    user,
  );
  if (type) {
    result.prices = result.prices.filter((price) => price.type === type);
  }
  return result;
};

export const putTeacherLessonPrices = async (
  teacherLessonPrices: TeacherLessonPricesDto,
  user: User,
): Promise<TeacherLessonPricesDto> => {
  return await doPut<TeacherLessonPricesDto>(
    `/teachers/${user.profile.sub}/lesson-prices`,
    user,
    teacherLessonPrices,
  );
};

export const getActionLink = async (
  user: User,
  actionId: string,
): Promise<TeacherActionResponse> => {
  return await doGet<TeacherActionResponse>(
    `/teachers/${user.profile.sub}/needed-actions/${actionId}`,
    user,
  );
};

const getDashboardLink = async (
  user: User,
): Promise<TeacherDashboardLinkDto> => {
  return await doGet<TeacherDashboardLinkDto>(
    `/teachers/${user.profile.sub}/dashboard-link`,
    user,
  );
};

export const putTeacherFeedback = async (
  user: User,
  teacherId: string,
  feedback: TeacherFeedbackDto,
): Promise<TeacherFeedbackDto> => {
  return await doPut<TeacherFeedbackDto>(
    `/teachers/${teacherId}/feedback/${user.profile.sub}`,
    user,
    feedback,
  );
};

export const getTeacherFeedback = async (
  user: User,
  teacherId: string,
): Promise<TeacherFeedbackDto> => {
  return await doGet<TeacherFeedbackDto>(
    `/teachers/${teacherId}/feedback/${user.profile.sub}`,
    user,
  );
};

export const getAllTeacherFeedback = async (
  user: User,
  teacherId: string,
): Promise<TeacherFeedbackResponseDto> => {
  return await doGet<TeacherFeedbackResponseDto>(
    `/teachers/${teacherId}/feedback`,
    user,
  );
};

export const useTeacherQuery = (
  teacherId?: string,
  options?: { enabled?: boolean },
) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["teacher", teacherId],
    queryFn: async () => {
      if (teacherId) {
        return await getTeacher(teacherId, user);
      }
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!teacherId,
  });
};

export const useAllTeacherFeedbackQuery = (teacherId?: string) => {
  return useAuthenticatedQuery(
    [...TEACHER_QUERY_KEYS.allTeacherFeedback, teacherId],
    async (user) => {
      if (teacherId) {
        return await getAllTeacherFeedback(user, teacherId);
      }
    },
  );
};

export const useTeacherFeedbackQuery = (teacherId?: string) => {
  return useAuthenticatedQuery(
    [...TEACHER_QUERY_KEYS.teacherFeedback, teacherId],
    async (user) => {
      if (teacherId) {
        return await getTeacherFeedback(user, teacherId);
      }
    },
  );
};

export const useTeacherLocationsQuery = (teacherId: string) => {
  return useAuthenticatedQuery(
    [...LOCATION_QUERY_KEYS.teacherLocations, teacherId],
    async (user) => {
      return await getTeacherLocations(user, teacherId);
    },
  );
};

export const useNeededActionsQuery = (isRedirected?: boolean) => {
  return useAuthenticatedQuery([NEEDED_ACTIONS_KEY], async (user) => {
    return await getNeededTeacherActions(user, isRedirected);
  });
};

export const useTeacherLessonPricesQuery = (
  teacherId?: string,
  type?: TeacherLessonPriceType,
) => {
  return useAuthenticatedQuery(
    [...TEACHER_QUERY_KEYS.teacherLessonPrices, teacherId],
    async (user) => {
      if (teacherId) {
        return await getTeacherLessonPrices(teacherId, user, type);
      }
      return await getTeacherLessonPrices(user.profile.sub, user, type);
    },
  );
};

export function useTeacherLessonPricesMutation() {
  return useAuthenticatedMutation(
    (teacherLessonPricesDto: TeacherLessonPricesDto, user: User) => {
      return putTeacherLessonPrices(teacherLessonPricesDto, user);
    },
    {
      invalidateKeys: [
        TEACHER_QUERY_KEYS.teacherLessonPrices,
        [NEEDED_ACTIONS_KEY],
      ],
    },
  );
}

export function useGetDashboardLinkMutation() {
  return useAuthenticatedMutation((_: void, user) => getDashboardLink(user));
}

export function useTeacherFeedbackMutation(teacherId: string) {
  return useAuthenticatedMutation(
    (teacherFeedbackDto: TeacherFeedbackDto, user: User) => {
      return putTeacherFeedback(user, teacherId, teacherFeedbackDto);
    },
    {
      invalidateKeys: [
        TEACHER_QUERY_KEYS.teacherFeedback,
        TEACHER_QUERY_KEYS.allTeacherFeedback,
      ],
    },
  );
}
